import { Quaternion, Vector3 } from 'three';
import { DEG2RAD } from '@constants/MathGeometry';
import type { FDI_Notation } from '@/common/FDI_Notation';
import {
  cloneConstraintsMap,
  DEFAULT_BEZIER_POINTS,
  DEFAULT_TREATMENT_CONSTRAINTS,
  type ToothConstraintValues,
} from '@correction/config/treatmentConstraints';

export interface iterationTransform {
  location: Vector3;
  rotation: Quaternion;
}

export default class TreatmentCyclesTransformation {
  private iterations: Map<number, iterationTransform[]> = new Map();
  private iterationsCount = 0;
  private constraints: Map<FDI_Notation, ToothConstraintValues>;
  private bezierControlPoints: [number, number, number, number];

  constructor(constraints?: Map<FDI_Notation, ToothConstraintValues>) {
    this.constraints = cloneConstraintsMap(constraints ?? DEFAULT_TREATMENT_CONSTRAINTS);
    this.bezierControlPoints = [...DEFAULT_BEZIER_POINTS];
    this.constraints.forEach((_, tooth) => {
      this.iterations.set(tooth, []);
    });
  }

  public resetIterations() {
    this.iterations.clear();
    this.constraints.forEach((_, tooth) => {
      this.iterations.set(tooth, []);
    });
    this.iterationsCount = 0;
  }

  /**
   * `orthoFrame` maps the tooth's orthodontic frame (X mesiodistal, Y long
   * axis, Z buccolingual) to world. The per-axis LIMIT comparison happens in
   * that frame — the constraint table is written orthodontically — while the
   * stored iteration steps stay world-space, because
   * applyIterationTransformations consumes them as world transforms.
   */
  public calculateIterations(
    toothFDI: number,
    locationOffset: Vector3,
    rotationOffset: Quaternion,
    orthoFrame?: Quaternion,
  ) {
    const constraint = this.constraints.get(toothFDI as FDI_Notation);
    if (!constraint) {
      console.warn(`No transformation constraints defined for tooth FDI ${toothFDI}`);
      this.iterations.delete(toothFDI);
      return;
    }

    const calcIterations = (absValue: number, limit: number) => {
      if (limit <= 0) return 0;
      if (absValue === 0) return 0;
      return absValue >= limit ? Math.ceil(absValue / limit) : 0;
    };

    const frameInverse = orthoFrame?.clone().invert() ?? null;
    const locationInFrame = frameInverse
      ? locationOffset.clone().applyQuaternion(frameInverse)
      : locationOffset;
    const translationAbs = new Vector3(
      Math.abs(locationInFrame.x),
      Math.abs(locationInFrame.y),
      Math.abs(locationInFrame.z),
    );
    const translationLimit = new Vector3(
      constraint.translationLimit.x,
      constraint.translationLimit.y,
      constraint.translationLimit.z,
    );

    const translationIterations = Math.max(
      calcIterations(translationAbs.x, translationLimit.x),
      calcIterations(translationAbs.y, translationLimit.y),
      calcIterations(translationAbs.z, translationLimit.z),
    );

    const rotationDelta = rotationOffset.clone().normalize();

    const w = Math.min(1, Math.max(-1, rotationDelta.w));
    let angle = 2 * Math.acos(w);
    if (angle > Math.PI) {
      angle = 2 * Math.PI - angle;
    }

    const sinHalf = Math.sqrt(Math.max(0, 1 - w * w));
    const axis = sinHalf > 1e-6
      ? new Vector3(rotationDelta.x, rotationDelta.y, rotationDelta.z).divideScalar(sinHalf)
      : new Vector3(0, 0, 0);
    if (frameInverse) {
      axis.applyQuaternion(frameInverse); // angle is frame-invariant, only the axis moves
    }
    const axisAbs = new Vector3(Math.abs(axis.x), Math.abs(axis.y), Math.abs(axis.z));
    const rotationContribution = axisAbs.multiplyScalar(angle);
    const rotationLimit = new Vector3(
      constraint.rotationLimitDeg.x * DEG2RAD,
      constraint.rotationLimitDeg.y * DEG2RAD,
      constraint.rotationLimitDeg.z * DEG2RAD,
    );

    const rotationIterations = Math.max(
      calcIterations(rotationContribution.x, rotationLimit.x),
      calcIterations(rotationContribution.y, rotationLimit.y),
      calcIterations(rotationContribution.z, rotationLimit.z),
    );

    const requiredIterations = Math.max(translationIterations, rotationIterations);

    if (requiredIterations === 0) {
      this.iterations.set(toothFDI, []);
      this.updateIterationsCount();
      return;
    }

    const iterationSteps: iterationTransform[] = [];
    const identityRotation = new Quaternion();
    const weights = this.getIterationWeights(requiredIterations);

    let cumulativeWeight = 0;
    for (let step = 0; step < requiredIterations; step++) {
      cumulativeWeight += weights[step] ?? 0;
      const cumulativeTranslation = locationOffset.clone().multiplyScalar(cumulativeWeight);
      const cumulativeRotation = new Quaternion().slerpQuaternions(identityRotation, rotationDelta, cumulativeWeight);
      iterationSteps.push({ location: cumulativeTranslation, rotation: cumulativeRotation });
    }

    this.iterations.set(toothFDI, iterationSteps);
    this.updateIterationsCount();
  }

  public getIterations() {
    return this.iterations;
  }

  public getIterationsCount() {
    return this.iterationsCount;
  }

  public getConstraints() {
    return cloneConstraintsMap(this.constraints);
  }

  public setConstraints(constraints: Map<FDI_Notation, ToothConstraintValues>) {
    this.constraints = cloneConstraintsMap(constraints);
    this.resetIterations();
  }

  public resetConstraints() {
    this.setConstraints(DEFAULT_TREATMENT_CONSTRAINTS);
  }

  public getBezierControlPoints(): [number, number, number, number] {
    return [...this.bezierControlPoints];
  }

  public setBezierControlPoints(points: [number, number, number, number]) {
    if (!Array.isArray(points) || points.length !== 4) return;
    this.bezierControlPoints = [...points] as [number, number, number, number];
    this.resetIterations();
  }

  public resetBezierControlPoints() {
    this.setBezierControlPoints(DEFAULT_BEZIER_POINTS);
  }

  private updateIterationsCount() {
    let maxIterations = 0;
    this.iterations.forEach((list) => {
      if (list.length > maxIterations) {
        maxIterations = list.length;
      }
    });
    this.iterationsCount = maxIterations;
  }

  private getIterationWeights(requiredIterations: number) {
    if (requiredIterations <= 0) return [] as number[];
    const weights: number[] = [];
    if (requiredIterations === 1) {
      return [1];
    }

    let previous = 0;
    for (let step = 1; step <= requiredIterations; step++) {
      const progress = step / requiredIterations;
      const current = this.evaluateBezier(progress);
      const delta = Math.max(0, current - previous);
      weights.push(delta);
      previous = current;
    }

    const sum = weights.reduce((acc, value) => acc + value, 0);
    if (sum <= 1e-6) {
      return Array(requiredIterations).fill(1 / requiredIterations);
    }
    return weights.map((value) => value / sum);
  }

  private evaluateBezier(t: number) {
    const [p1x, p1y, p2x, p2y] = this.bezierControlPoints;
    const clamp = (num: number) => Math.min(1, Math.max(0, num));
    let param = clamp(t);

    for (let i = 0; i < 5; i++) {
      const xEstimate = this.cubicBezier(param, 0, p1x, p2x, 1);
      const error = xEstimate - t;
      if (Math.abs(error) < 1e-5) break;
      const derivative = this.cubicBezierDerivative(param, 0, p1x, p2x, 1);
      if (Math.abs(derivative) < 1e-6) break;
      param -= error / derivative;
      param = clamp(param);
    }

    return this.cubicBezier(param, 0, p1y, p2y, 1);
  }

  private cubicBezier(t: number, p0: number, p1: number, p2: number, p3: number) {
    const u = 1 - t;
    return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
  }

  private cubicBezierDerivative(t: number, p0: number, p1: number, p2: number, p3: number) {
    const u = 1 - t;
    return (
      3 * u * u * (p1 - p0) +
      6 * u * t * (p2 - p1) +
      3 * t * t * (p3 - p2)
    );
  }
}
